import { sortingPriorityObj } from "components/FieldTypes/TableField/TableField";

interface MultiSortProps {
	array: Record<string, undefined | null | string | number>[];
	sortingPriority: sortingPriorityObj[];
}

// Utility type: only keys whose values are strings or dates
type SortableKeys<T> = {
	[K in keyof T]: T[K] extends string | Date ? K : never;
}[keyof T];

interface SortProps<T> {
	array: T[];
	key?: SortableKeys<T>;
	descending?: boolean;
}

export function sortByAlphaNumericalArray({ array, descending }: { array: string[]; descending?: boolean }): string[];

export function sortByAlphaNumericalArray<T extends Record<string, any>>({ array, key, descending }: SortProps<T>): T[];

export function sortByAlphaNumericalArray<T>({ array, key, descending }: SortProps<T>) {
	const sorted = [...array].sort((a, b) => {
		const aVal = key ? ((a as T)[key] as string) : (a as string);
		const bVal = key ? ((b as T)[key] as string) : (b as string);
		if (descending) {
			return bVal.localeCompare(aVal, undefined, {
				numeric: true,
				sensitivity: "base",
			});
		}
		return aVal.localeCompare(bVal, undefined, {
			numeric: true,
			sensitivity: "base",
		});
	});

	return sorted as T[];
}

export function sortByDate({ array, descending }: { array: Date[]; descending?: boolean }): Date[];

export function sortByDate<T extends Record<string, any>>({ array, key, descending }: SortProps<T>): T[];

export function sortByDate<T>({ array, key, descending }: SortProps<T>) {
	array.sort((a, b) => {
		const aVal = key ? new Date((a as T)[key] as string | Date) : (a as Date);
		const bVal = key ? new Date((b as T)[key] as string | Date) : (b as Date);
		if (descending) {
			return bVal.getTime() - aVal.getTime();
		}
		return aVal.getTime() - bVal.getTime();
	});

	return array as T[];
}

// Going to try sorting by lowest priority then work our way up from there.  So thats what this function is doing.
export const multiLevelSorting = ({ array, sortingPriority }: MultiSortProps) => {
	if (array.length === 0) return array;

	// Sort out the sortingPriority by rank here!  We are sorting it so the lowest rank is first.
	let sortedSortingPriority = sortingPriority.sort((a, b) => {
		return b.rank - a.rank;
	});

	for (let idx = 0; idx < sortedSortingPriority.length; idx++) {
		const currentSortingPriority = sortedSortingPriority[idx];
		array = array.sort((a, b) => {
			let valueA = a[currentSortingPriority.identifier];
			let valueB = b[currentSortingPriority.identifier];

			if (!valueB) {
				return -1;
			}
			if (!valueA) {
				return 1;
			}

			if (sortedSortingPriority[idx].date) {
				valueA = new Date(valueA).getTime();
				valueB = new Date(valueB).getTime();
			}

			valueA = valueA.toString();
			valueB = valueB.toString();
			const outcome = currentSortingPriority.descending
				? valueB.localeCompare(valueA, undefined, { numeric: true, sensitivity: "base" })
				: valueA.localeCompare(valueB, undefined, { numeric: true, sensitivity: "base" });
			return outcome;
		});
	}
	return array;
};
